// @ts-nocheck
/**
 * EWMap — EARLYWIRE plotting-board render engine (Web Mercator on <canvas>).
 *
 * PORTED VERBATIM from the proven prototype for performance fidelity. It is
 * intentionally left loosely typed (`@ts-nocheck`) because it is hand-tuned
 * imperative canvas code where strict typing adds churn without safety; the
 * PUBLIC surface is typed via `EWMapOptions` below and the data it consumes is
 * the typed `TrackedObject` / `GeoEvent` / `Signal` from @/types/api.
 *
 * Performance pipeline (do not "simplify" without profiling):
 *  · CARTO raster tiles grayscaled + phosphor-tinted ONCE at load
 *  · static layer (tiles/graticule/land/rulers) cached offscreen
 *  · object layer (10k–120k markers) cached offscreen; re-rasters ≤30fps
 *  · per-frame steady state = two drawImage blits + ~100 animated markers
 *  · spatial grid viewport culling + LOD stride + 36-angle icon sprite atlas
 *  · inline-Mercator projection cache (no d3 call per object per frame)
 */
import * as d3 from "d3-geo";
import * as topojson from "topojson-client";
import { SEVERITY_COLOR, SEVERITY_RANK } from "@/types/api";
import type { GeoEvent, Signal, TrackedObject } from "@/types/api";
import { config } from "@/config";

export interface EWMapColors {
  phosphor: string; // "r, g, b"
  severity: Record<string, string>;
}
export interface EWMapOptions {
  reduced?: boolean;
  live?: boolean;
  objects?: TrackedObject[];
  events?: GeoEvent[];
  signals?: Signal[];
  colors?: EWMapColors;
  /** event types to render; null = all (the data layer always streams all types) */
  enabledEventTypes?: Set<string> | null;
}

/**
 * EWMapApi — the typed PUBLIC surface the React layer consumes via `getMap()`.
 * The engine body is `@ts-nocheck` (hand-tuned canvas code), so its members are
 * not inferred for importers; this interface is the contract. Keep in sync with
 * the public methods/fields actually used by `useOps` / `MapView` / `BootLoader`.
 */
export interface EWMapApi {
  /** canvas CSS-pixel dimensions (set on resize) */
  W: number;
  H: number;
  /** target zoom scale */
  tk: number;
  /** true while a pointer drag is in progress (suppresses click/hover) */
  dragMoved: boolean;
  resize(): void;
  markPositionsChanged(): void;
  setLayer(k: string, on: boolean): void;
  /** restrict the rendered/pickable event set to these types (null = all) */
  setEventTypes(types: Set<string> | null): void;
  select(id: string | null): void;
  selectEvent(id: string | null): void;
  flyTo(lat: number, lon: number, k?: number): void;
  overview(): void;
  zoomAt(factor: number, px: number, py: number): void;
  pick(mx: number, my: number, radius?: number): Signal | null;
  pickEvent(mx: number, my: number, radius?: number): GeoEvent | null;
  /** fraction (0..1) of tiles for the current view that are decoded + tinted */
  _tilesReady(): number;
}

const TAU = Math.PI * 2;

// Stable per-id hash (FNV-1a) → deterministic LOD selection: the SAME objects
// persist across zoom/pan instead of a different random subset each re-raster.
function lhash(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

export class EWMap {
    constructor(canvas, opts = {}) {
      this.c = canvas;
      this.ctx = canvas.getContext("2d");
      this.reduced = opts.reduced || false;
      this.live = opts.live || false;
      this.colors = opts.colors;
      this.layers = { basemap: true, maritime: false, aircraft: false, satellite: false, events: true };
      this.objects = opts.objects || [];
      this.events = opts.events || [];
      this.enabledEventTypes = opts.enabledEventTypes ?? null; // null = render all types
      this.signals = opts.signals || [];
      this.selectedId = null;
      this.selectedEventId = null;
      this.trails = new Map();
      // spatial grid (viewport culling for 100k+ objects) + LOD budget
      this.GRIDN = 96;
      this._grid = null;
      this._gridDirty = true;
      this._gridAt = 0;
      this._budget = 32000;          // max markers drawn at world view (LOD stride)
      this.posVersion = 0;           // bumped when positions change → forces re-raster
      this.k = 1; this.tx = 0; this.ty = 0;
      this.tk = 1; this.ttx = 0; this.tty = 0;
      this.t = 0;
      this.land = null;
      this._tiles = new Map();   // key → {img, cv}
      this._tileGen = 0;
      this._glows = new Map();
      this._static = document.createElement("canvas");
      this._obj = document.createElement("canvas");
      this.resize();
      window.addEventListener("resize", () => this.resize());
      this._initInput();
      this._load();
      requestAnimationFrame((ts) => this.loop(ts));
    }

    async _load() {
      try {
        const topo = await fetch("https://unpkg.com/world-atlas@2/land-110m.json").then((r) => r.json());
        this.land = topojson.feature(topo, topo.objects.land);
      } catch (e) { this.land = null; }
      this._fit(); this._lastDataSig = null;
    }

    resize() {
      let cLat, cLon, hadCenter = false;
      if (this.proj && this.proj.invert && this.k > 1.001) {
        const cx = (this.W / 2 - this.tx) / this.k;
        const cy = (this.H / 2 - this.ty) / this.k;
        const inv = this.proj.invert([cx, cy]);
        if (inv && isFinite(inv[0]) && isFinite(inv[1])) {
          cLon = inv[0]; cLat = inv[1]; hadCenter = true;
        }
      }
      const dpr = this.dpr = Math.min(2, window.devicePixelRatio || 1);
      this.W = this.c.clientWidth; this.H = this.c.clientHeight;
      this.c.width = this.W * dpr; this.c.height = this.H * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      for (const cv of [this._static, this._obj]) { cv.width = this.W * dpr; cv.height = this.H * dpr; }
      this._sctx = this._static.getContext("2d"); this._sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this._octx = this._obj.getContext("2d"); this._octx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this._fit();
      if (hadCenter) {
        const p = this.proj([cLon, cLat]);
        this.tx = this.ttx = this.W / 2 - this.k * p[0];
        this.ty = this.tty = this.H / 2 - this.k * p[1];
      }
      this._lastDataSig = null; this._rk = null;
    }

    _fit() {
      if (!this.W) return;
      this.S0 = this.W;                       // world pixel width at k = 1
      this.yoff = (this.H - this.S0) / 2;
      this.proj = d3.geoMercator()
        .scale(this.S0 / (2 * Math.PI))
        .translate([this.S0 / 2, this.S0 / 2 + this.yoff]);
      // inline-Mercator constants (avoids a d3 call + array alloc per object per render)
      this._mScale = this.S0 / (2 * Math.PI);
      this._mTx = this.S0 / 2;
      this._mTy = this.S0 / 2 + this.yoff;
      this._cell = this.S0 / this.GRIDN;
      this._gridDirty = true;
      for (const o of this.objects) o._wlon = undefined; // invalidate world-coord cache
      this.pathS = d3.geoPath(this.proj, this._sctx);
      this.graticule = d3.geoGraticule10();
    }

    getBounds() {
      if (!this.proj || !this.proj.invert) return null;
      const tl = this.proj.invert([(0 - this.tx) / this.k, (0 - this.ty) / this.k]);
      const br = this.proj.invert([(this.W - this.tx) / this.k, (this.H - this.ty) / this.k]);
      if (!tl || !br || !isFinite(tl[0]) || !isFinite(br[0])) return null;
      return [
        Math.max(-180, Math.min(tl[0], br[0])), Math.max(-85, Math.min(tl[1], br[1])),
        Math.min(180, Math.max(tl[0], br[0])), Math.min(85, Math.max(tl[1], br[1])),
      ];
    }

    markPositionsChanged() { this.posVersion++; this._gridDirty = true; }

    // (re)build the spatial grid: bucket every object into a world-pixel cell.
    // O(n) but amortized — only when positions change, throttled to ~2.5/s.
    _buildGrid() {
      const N = this.GRIDN, cell = this._cell, yoff = this.yoff;
      const grid = new Array(N * N);
      for (const o of this.objects) {
        if (o._wlon !== o.lon || o._wlat !== o.lat) this._wc(o);
        let gx = (o._wx / cell) | 0;
        let gy = ((o._wy - yoff) / cell) | 0;
        if (gx < 0) gx = 0; else if (gx >= N) gx = N - 1;
        if (gy < 0) gy = 0; else if (gy >= N) gy = N - 1;
        const idx = gy * N + gx;
        (grid[idx] || (grid[idx] = [])).push(o);
      }
      this._grid = grid;
      this._gridDirty = false;
    }
    _ensureGrid(now) {
      if (this._grid && !this._gridDirty) return;
      if (this._grid && now - this._gridAt < 400) return; // throttle rebuilds
      this._buildGrid();
      this._gridAt = now || performance.now();
    }
    // list of populated buckets intersecting the current viewport
    _visibleCells() {
      const N = this.GRIDN, cell = this._cell, k = this.k, yoff = this.yoff;
      const wx0 = (-this.tx) / k, wx1 = (this.W - this.tx) / k;
      const wy0 = (-this.ty) / k - yoff, wy1 = (this.H - this.ty) / k - yoff;
      let cx0 = (wx0 / cell - 1) | 0, cx1 = (wx1 / cell + 1) | 0;
      let cy0 = (wy0 / cell - 1) | 0, cy1 = (wy1 / cell + 1) | 0;
      if (cx0 < 0) cx0 = 0; if (cy0 < 0) cy0 = 0;
      if (cx1 >= N) cx1 = N - 1; if (cy1 >= N) cy1 = N - 1;
      const out = [];
      const grid = this._grid;
      for (let gy = cy0; gy <= cy1; gy++) {
        const row = gy * N;
        for (let gx = cx0; gx <= cx1; gx++) {
          const b = grid[row + gx];
          if (b) out.push(b);
        }
      }
      return out;
    }

    setColors(c) { this.colors = c; this._tiles.clear(); this._glows.clear(); this._lastDataSig = null; }
    setLayer(k, on) { this.layers[k] = on; this._lastDataSig = null; }
    setEventTypes(s) { this.enabledEventTypes = s; }

    // fraction of tiles needed for the CURRENT view that are decoded & tinted (0..1)
    _tilesReady() {
      if (!this.layers.basemap || !this.W) return 1;
      const S = this.S0 * this.k;
      const z = Math.max(1, Math.min(13, Math.round(Math.log2(S / 256))));
      const n = 1 << z, ts = S / n;
      const ox = this.tx, oy = this.ty + this.yoff * this.k;
      const x0 = Math.max(0, Math.floor(-ox / ts)), x1 = Math.min(n - 1, Math.floor((this.W - ox) / ts));
      const y0 = Math.max(0, Math.floor(-oy / ts)), y1 = Math.min(n - 1, Math.floor((this.H - oy) / ts));
      let need = 0, have = 0;
      for (let tx = x0; tx <= x1; tx++) for (let ty = y0; ty <= y1; ty++) {
        need++;
        const rec = this._tiles.get(z + "/" + tx + "/" + ty);
        if (rec && rec.cv) have++;
      }
      return need ? have / need : 1;
    }
    select(id) { this.selectedId = id; }
    selectEvent(id) { this.selectedEventId = id; }

    flyTo(lat, lon, k = 3.6) {
      if (!this.proj) return;
      const p = this.proj([lon, lat]);
      this.tk = k;
      this.ttx = this.W / 2 - k * p[0];
      this.tty = this.H / 2 - k * p[1];
    }
    overview() { this.tk = 1; this.ttx = 0; this.tty = 0; }
    zoomAt(factor, px, py) {
      if (!this.proj) return;
      const nk = Math.max(1, Math.min(64, this.tk * factor));
      const baseX = (px - this.tx) / this.k, baseY = (py - this.ty) / this.k;
      this.tk = nk;
      this.ttx = px - nk * baseX;
      this.tty = py - nk * baseY;
      this._clampTarget();
    }
    panBy(dx, dy) {
      this.tx += dx; this.ty += dy; this.ttx += dx; this.tty += dy;
      this._clampTarget();
    }
    _clampTarget() { if (this.tk <= 1.001) { this.ttx = 0; this.tty = 0; } }

    _initInput() {
      const c = this.c;
      this.dragMoved = false;
      c.addEventListener("wheel", (e) => {
        e.preventDefault();
        const r = c.getBoundingClientRect();
        const px = (e.clientX - r.left) * (this.W / r.width);
        const py = (e.clientY - r.top) * (this.H / r.height);
        this.zoomAt(e.deltaY < 0 ? 1.25 : 1 / 1.25, px, py);
      }, { passive: false });
      let drag = null;
      c.addEventListener("mousedown", (e) => {
        const r = c.getBoundingClientRect();
        drag = { x: e.clientX, y: e.clientY, s: this.W / r.width };
        this.dragMoved = false; c.style.cursor = "grabbing";
      });
      window.addEventListener("mousemove", (e) => {
        if (!drag) return;
        if (Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y) > 3) this.dragMoved = true;
        this.panBy((e.clientX - drag.x) * drag.s, (e.clientY - drag.y) * drag.s);
        drag.x = e.clientX; drag.y = e.clientY;
      });
      window.addEventListener("mouseup", () => { drag = null; c.style.cursor = "crosshair"; });
    }

    screen(lon, lat) {
      const p = this.proj([lon, lat]);
      return [this.tx + this.k * p[0], this.ty + this.k * p[1]];
    }
    // cached world projection — recomputed only when the object's lat/lon changes
    _wc(o) {
      if (o._wlon !== o.lon || o._wlat !== o.lat) {
        const lat = o.lat > 85 ? 85 : o.lat < -85 ? -85 : o.lat;
        o._wx = this._mScale * (o.lon * 0.017453292519943295) + this._mTx;
        o._wy = this._mTy - this._mScale * Math.log(Math.tan(0.7853981633974483 + lat * 0.008726646259971648));
        o._wlon = o.lon; o._wlat = o.lat;
      }
    }
    _inView(x, y, m = 10) { return x >= -m && x <= this.W + m && y >= -m && y <= this.H + m; }

    /* ---------- tiles (tinted once at load) ---------- */
    _tile(z, x, y) {
      const key = z + "/" + x + "/" + y;
      let rec = this._tiles.get(key);
      if (!rec) {
        if (this._tiles.size > 420) { const k0 = this._tiles.keys().next().value; this._tiles.delete(k0); }
        const img = new Image();
        rec = { img, cv: null };
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const g = this.colors ? this.colors.phosphor : "95, 211, 224";
          const cv = document.createElement("canvas");
          cv.width = cv.height = 256;
          const t = cv.getContext("2d");
          t.filter = "grayscale(1) brightness(1.55) contrast(1.12)";
          t.drawImage(img, 0, 0, 256, 256);
          t.filter = "none";
          t.globalCompositeOperation = "multiply";
          t.fillStyle = `rgba(${g}, 0.5)`;
          t.fillRect(0, 0, 256, 256);
          rec.cv = cv;
          this._tileGen++;
        };
        img.src = config.tileUrlTemplate.replace("{s}", "abcd"[(x + y) % 4]).replace("{z}", String(z)).replace("{x}", String(x)).replace("{y}", String(y));
        this._tiles.set(key, rec);
      }
      return rec;
    }

    /* ---------- layer signatures (data only, NOT transform) ---------- */
    _dataSig() {
      return this._tileGen + "|" + this.objects.length + "|" + this.posVersion + "|" + (this.land ? 1 : 0) + "|" +
        this.layers.basemap + this.layers.maritime + this.layers.aircraft + this.layers.satellite;
    }

    /* ---------- static layer: tiles + graticule + land + rulers ---------- */
    _renderStatic() {
      const ctx = this._sctx, W = this.W, H = this.H;
      const g = this.colors ? this.colors.phosphor : "95, 211, 224";
      ctx.clearRect(0, 0, W, H);
      if (this.layers.basemap) {
        ctx.fillStyle = "#081009";
        ctx.fillRect(0, 0, W, H);
        const S = this.S0 * this.k;
        const z = Math.max(1, Math.min(13, Math.round(Math.log2(S / 256))));
        const n = 1 << z, ts = S / n;
        const ox = this.tx, oy = this.ty + this.yoff * this.k;
        const x0 = Math.max(0, Math.floor(-ox / ts)), x1 = Math.min(n - 1, Math.floor((W - ox) / ts));
        const y0 = Math.max(0, Math.floor(-oy / ts)), y1 = Math.min(n - 1, Math.floor((H - oy) / ts));
        for (let tx = x0; tx <= x1; tx++) {
          for (let ty = y0; ty <= y1; ty++) {
            const rec = this._tile(z, tx, ty);
            if (rec.cv) ctx.drawImage(rec.cv, ox + tx * ts, oy + ty * ts, ts + 0.6, ts + 0.6);
          }
        }
        const px0 = Math.max(0, x0 - 2), px1 = Math.min(n - 1, x1 + 2);
        const py0 = Math.max(0, y0 - 2), py1 = Math.min(n - 1, y1 + 2);
        for (let tx = px0; tx <= px1; tx++) for (let ty = py0; ty <= py1; ty++) {
          if (tx >= x0 && tx <= x1 && ty >= y0 && ty <= y1) continue;
          this._tile(z, tx, ty);
        }
      }
      // vector chrome
      ctx.save();
      ctx.translate(this.tx, this.ty);
      ctx.scale(this.k, this.k);
      ctx.beginPath(); this.pathS(this.graticule);
      ctx.lineWidth = 0.6 / this.k;
      ctx.strokeStyle = `rgba(${g}, ${this.layers.basemap ? 0.07 : 0.10})`;
      ctx.stroke();
      if (this.land) {
        ctx.beginPath(); this.pathS(this.land);
        if (!this.layers.basemap) { ctx.fillStyle = `rgba(${g}, 0.05)`; ctx.fill(); }
        ctx.lineWidth = (this.layers.basemap ? 1.1 : 1.0) / this.k;
        ctx.strokeStyle = `rgba(${g}, ${this.layers.basemap ? 0.42 : 0.5})`;
        ctx.stroke();
      }
      ctx.beginPath(); this.pathS({ type: "Sphere" });
      ctx.lineWidth = 1 / this.k;
      ctx.strokeStyle = `rgba(${g}, 0.28)`;
      ctx.stroke();
      ctx.restore();
      this._drawRulers(ctx, g);
    }

    _drawRulers(ctx, g) {
      const W = this.W, H = this.H;
      const b = d3.geoPath(this.proj).bounds({ type: "Sphere" });
      const X0 = this.tx + this.k * b[0][0], Y0raw = this.ty + this.k * b[0][1];
      const X1 = this.tx + this.k * b[1][0];
      const Y0 = Math.max(20, Y0raw);
      ctx.strokeStyle = `rgba(${g}, 0.12)`;
      ctx.lineWidth = 1;
      ctx.strokeRect(8.5, 8.5, W - 17, H - 17);
      const cm = 9;
      ctx.strokeStyle = `rgba(${g}, 0.3)`;
      [[10, 10, 1, 1], [W - 10, 10, -1, 1], [10, H - 10, 1, -1], [W - 10, H - 10, -1, -1]].forEach(([cx, cy, sx, sy]) => {
        ctx.beginPath();
        ctx.moveTo(cx, cy + sy * cm); ctx.lineTo(cx, cy); ctx.lineTo(cx + sx * cm, cy);
        ctx.stroke();
      });
      ctx.font = "9px 'IBM Plex Mono', monospace";
      ctx.fillStyle = `rgba(${g}, 0.45)`;
      ctx.strokeStyle = `rgba(${g}, 0.25)`;
      ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
      for (let lon = -150; lon <= 180; lon += 30) {
        const x = this.screen(lon, 0)[0];
        if (x < X0 - 1 || x > X1 + 1 || x < 14 || x > W - 14) continue;
        ctx.beginPath(); ctx.moveTo(x, Y0 + 14); ctx.lineTo(x, Y0 + 9); ctx.stroke();
        ctx.fillText(lon === 0 ? "0°" : Math.abs(lon) + (lon > 0 ? "E" : "W"), x, Y0 + 26);
      }
      ctx.textAlign = "right"; ctx.textBaseline = "middle";
      for (let lat = -60; lat <= 60; lat += 30) {
        const y = this.screen(0, lat)[1];
        if (y < 16 || y > H - 16) continue;
        const xr = Math.max(36, X0 + 14);
        ctx.beginPath(); ctx.moveTo(xr - 5, y); ctx.lineTo(xr, y); ctx.stroke();
        ctx.fillText(lat === 0 ? "0°" : Math.abs(lat) + (lat > 0 ? "N" : "S"), xr - 8, y);
      }
      ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
    }

    /* ---------- trails ---------- */
    _recordTrails() {
      // Only track objects currently in view, via the spatial grid (cheap at 100k+).
      const tx = this.tx, ty = this.ty, k = this.k, W = this.W, H = this.H;
      const cells = this._grid ? this._visibleCells() : null;
      const seen = new Set();
      const rec = (o) => {
        const x = tx + k * o._wx, y = ty + k * o._wy;
        if (x < -20 || x > W + 20 || y < -20 || y > H + 20) return;
        seen.add(o.id);
        let tr = this.trails.get(o.id);
        if (!tr) { this.trails.set(o.id, { pts: [], lastLat: o.lat, lastLon: o.lon, headRad: null }); return; }
        const dLat = o.lat - tr.lastLat, dLon = o.lon - tr.lastLon;
        const d = Math.abs(dLat) + Math.abs(dLon);
        if (d > 2.5) { tr.pts.length = 0; tr.lastLat = o.lat; tr.lastLon = o.lon; return; }
        if (d > 0.008) {
          tr.pts.push({ lat: tr.lastLat, lon: tr.lastLon });
          if (tr.pts.length > 12) tr.pts.shift();
          tr.headRad = Math.atan2(dLon, dLat);
          tr.lastLat = o.lat; tr.lastLon = o.lon;
        }
      };
      if (cells) { for (let c = 0; c < cells.length; c++) { const b = cells[c]; for (let j = 0; j < b.length; j++) { const o = b[j]; if (o.domain !== "satellite") rec(o); } } }
      else { for (const o of this.objects) { if (o.domain !== "satellite") { this._wc(o); rec(o); } } }
      // prune trails for objects no longer in view
      if (this.trails.size > Math.max(1500, seen.size * 2)) {
        for (const id of this.trails.keys()) if (!seen.has(id)) this.trails.delete(id);
      }
    }
    _heading(o) {
      if (typeof o.heading === "number" && isFinite(o.heading)) return o.heading * Math.PI / 180;
      const tr = this.trails.get(o.id);
      return (tr && tr.headRad != null) ? tr.headRad : 0;
    }

    /* ---------- object layer (vessels / aircraft / satellites + trails) ---------- */
    _renderObjects() {
      const ctx = this._octx, k = this.k;
      const tx = this.tx, ty = this.ty, W = this.W, H = this.H;
      const g = this.colors ? this.colors.phosphor : "95, 211, 224";
      ctx.clearRect(0, 0, this.W, this.H);
      const icons = k >= 2.6, trailsOn = k >= 2.6, labelsOn = k >= 9;
      const aSize = Math.min(8, 1.6 + k * 0.4);
      const mSize = Math.min(6.5, 1.4 + k * 0.32);
      const dotR = Math.min(1.6, 0.7 + k * 0.18);
      const triS = Math.min(2.1, 0.9 + k * 0.28);
      const dotA = k < 1.6 ? 0.42 : 0.62;
      const labelCell = 64, labelSeen = new Set();
      const labelQueue = [];

      this._ensureGrid(performance.now());
      const cells = this._grid ? this._visibleCells() : null;

      // Show ALL visible objects up to a generous cap. Zoomed in, the viewport
      // bounds the count so this is effectively "all"; only an extreme world view
      // exceeds it. Beyond the cap we apply a STABLE power-of-two stride keyed on
      // a per-object hash, so sparser/denser views are nested subsets — the same
      // entities persist instead of swapping as you zoom or pan. (Runs only on
      // re-raster: zoom/pan/data change, not per frame — the layer is cached.)
      let visN = 0;
      if (cells) for (let i = 0; i < cells.length; i++) visN += cells[i].length;
      else visN = this.objects.length;
      const budget = icons ? 24000 : 80000;
      let stride = 1;
      if (visN > budget) { const need = Math.ceil(visN / budget); while (stride < need) stride <<= 1; }
      const mask = stride - 1, useStride = stride > 1;

      // helper: iterate visible objects (grid-culled when available), keeping a
      // stable hash-selected subset when over budget (no identity swapping).
      const visit = (fn) => {
        if (cells) {
          for (let c = 0; c < cells.length; c++) {
            const b = cells[c];
            for (let j = 0; j < b.length; j++) {
              const o = b[j];
              if (useStride) { if (o._lh === undefined) o._lh = lhash(o.id); if (o._lh & mask) continue; }
              fn(o);
            }
          }
        } else {
          for (const o of this.objects) {
            if (useStride) { if (o._lh === undefined) o._lh = lhash(o.id); if (o._lh & mask) continue; }
            fn(o);
          }
        }
      };

      if (!icons) {
        // world view — batched fillRect dust, two style passes over visible cells
        if (this.layers.maritime) {
          ctx.fillStyle = `rgba(${g}, ${dotA})`;
          const sz = dotR < 1 ? 1.5 : dotR * 2, h = sz / 2;
          visit((o) => {
            if (o.domain !== "maritime") return;
            const x = tx + k * o._wx, y = ty + k * o._wy;
            if (x < -10 || x > W + 10 || y < -10 || y > H + 10) return;
            ctx.fillRect(x - h, y - h, sz, sz);
          });
        }
        if (this.layers.aircraft) {
          ctx.fillStyle = `rgba(214, 163, 110, ${k < 1.6 ? 0.55 : 0.75})`;
          const sz = triS < 1.2 ? 1.8 : triS * 1.7, h = sz / 2;
          visit((o) => {
            if (o.domain !== "aircraft") return;
            const x = tx + k * o._wx, y = ty + k * o._wy;
            if (x < -10 || x > W + 10 || y < -10 || y > H + 10) return;
            ctx.fillRect(x - h, y - h, sz, sz);
          });
        }
      } else {
        // zoomed — glyph icons via pre-rendered rotation atlas (drawImage blits)
        const atM = this.layers.maritime ? this._iconAtlas("maritime") : null;
        const atA = this.layers.aircraft ? this._iconAtlas("aircraft") : null;
        const refS = this.ICON_REF_S || 7, REF = 20;
        const wM = REF * (mSize / refS), wA = REF * (aSize / refS);
        const hM = wM / 2, hA = wA / 2;
        visit((o) => {
          if (!this.layers[o.domain] || o.domain === "satellite") return;
          const x = tx + k * o._wx, y = ty + k * o._wy;
          if (x < -10 || x > W + 10 || y < -10 || y > H + 10) return;
          if (trailsOn) this._drawTrail(ctx, o, g, x, y);
          let rot = this._heading(o); rot %= TAU; if (rot < 0) rot += TAU;
          const bk = ((rot / TAU) * 36) | 0;
          if (o.domain === "maritime") { if (atM) ctx.drawImage(atM[bk], x - hM, y - hM, wM, wM); }
          else if (atA) ctx.drawImage(atA[bk], x - hA, y - hA, wA, wA);
          if (labelsOn && o.name) {
            const lk = ((x / labelCell) | 0) * 8192 + ((y / labelCell) | 0);
            if (!labelSeen.has(lk)) { labelSeen.add(lk); labelQueue.push([o, x, y]); }
          }
        });
      }
      if (this.layers.satellite) {
        ctx.strokeStyle = `rgba(${g}, 0.32)`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        let batch = 0;
        for (const o of this.objects) {
          if (o.domain !== "satellite") continue;
          this._wc(o);
          const x = tx + k * o._wx, y = ty + k * o._wy;
          if (x < -10 || x > W + 10 || y < -10 || y > H + 10) continue;
          ctx.moveTo(x - 2, y); ctx.lineTo(x + 2, y);
          ctx.moveTo(x, y - 2); ctx.lineTo(x, y + 2);
          if (++batch === 256) { ctx.stroke(); ctx.beginPath(); batch = 0; }
        }
        if (batch) ctx.stroke();
      }
      // pass 2: labels on top
      if (labelQueue.length) {
        ctx.font = "9px 'IBM Plex Mono', monospace";
        for (const [o, x, y] of labelQueue) {
          ctx.fillStyle = o.domain === "aircraft" ? "rgba(214,163,110,0.62)" : `rgba(${g},0.55)`;
          ctx.fillText(o.name, x + 10, y - 7);
        }
      }
    }

    _drawTrail(ctx, o, g, hx, hy) {
      const tr = this.trails.get(o.id);
      if (!tr || !tr.pts.length) return;
      ctx.lineWidth = 1;
      const col = o.domain === "aircraft" ? "214,163,110" : g;
      let px = hx, py = hy;
      for (let i = tr.pts.length - 1, seg = 0; i >= 0; i--, seg++) {
        const a = 0.38 - seg * 0.045;
        if (a <= 0.02) break;
        const [qx, qy] = this.screen(tr.pts[i].lon, tr.pts[i].lat);
        if (Math.hypot(qx - px, qy - py) > 260) break;
        ctx.strokeStyle = `rgba(${col},${a})`;
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(qx, qy); ctx.stroke();
        px = qx; py = qy;
      }
    }

    _planePath(ctx, s) {
      ctx.beginPath();
      ctx.moveTo(0, -1.05 * s);
      ctx.lineTo(0.16 * s, -0.5 * s); ctx.lineTo(0.95 * s, 0.08 * s); ctx.lineTo(0.95 * s, 0.32 * s);
      ctx.lineTo(0.14 * s, 0.12 * s); ctx.lineTo(0.12 * s, 0.6 * s); ctx.lineTo(0.38 * s, 0.86 * s);
      ctx.lineTo(0.38 * s, 1.02 * s); ctx.lineTo(0, 0.88 * s); ctx.lineTo(-0.38 * s, 1.02 * s);
      ctx.lineTo(-0.38 * s, 0.86 * s); ctx.lineTo(-0.12 * s, 0.6 * s); ctx.lineTo(-0.14 * s, 0.12 * s);
      ctx.lineTo(-0.95 * s, 0.32 * s); ctx.lineTo(-0.95 * s, 0.08 * s); ctx.lineTo(-0.16 * s, -0.5 * s);
      ctx.closePath();
    }
    _shipPath(ctx, s) {
      ctx.beginPath();
      ctx.moveTo(0, -1.1 * s);
      ctx.lineTo(0.45 * s, -0.35 * s); ctx.lineTo(0.45 * s, 0.95 * s);
      ctx.lineTo(-0.45 * s, 0.95 * s); ctx.lineTo(-0.45 * s, -0.35 * s);
      ctx.closePath();
    }
    // 36-angle pre-rendered glyph atlas → blit with drawImage (no per-marker rotate/path)
    _iconAtlas(domain) {
      const g = this.colors ? this.colors.phosphor : "95, 211, 224";
      const key = domain + "|" + g;
      this._atlas = this._atlas || new Map();
      let arr = this._atlas.get(key);
      if (arr) return arr;
      const REF = 20, half = REF / 2, dpr = this.dpr;
      const s = this.ICON_REF_S = 7;             // glyph half-scale baked into the atlas
      const fill = domain === "aircraft" ? "rgba(214, 163, 110, 0.9)" : `rgba(${g}, 0.82)`;
      arr = [];
      for (let a = 0; a < 36; a++) {
        const cv = document.createElement("canvas");
        cv.width = cv.height = REF * dpr;
        const c = cv.getContext("2d");
        c.setTransform(dpr, 0, 0, dpr, 0, 0);
        c.translate(half, half); c.rotate(a * Math.PI / 18);
        c.fillStyle = fill;
        if (domain === "aircraft") this._planePath(c, s); else this._shipPath(c, s);
        c.fill();
        arr.push(cv);
      }
      this._atlas.set(key, arr);
      return arr;
    }

    _planeIcon(ctx, x, y, rot, s, fill) {
      ctx.save(); ctx.translate(x, y); ctx.rotate(rot);
      ctx.fillStyle = fill; this._planePath(ctx, s); ctx.fill();
      ctx.restore();
    }
    _shipIcon(ctx, x, y, rot, s, fill) {
      ctx.save(); ctx.translate(x, y); ctx.rotate(rot);
      ctx.beginPath();
      ctx.moveTo(0, -1.1 * s);
      ctx.lineTo(0.45 * s, -0.35 * s); ctx.lineTo(0.45 * s, 0.95 * s);
      ctx.lineTo(-0.45 * s, 0.95 * s); ctx.lineTo(-0.45 * s, -0.35 * s);
      ctx.closePath();
      ctx.fillStyle = fill; ctx.fill();
      ctx.restore();
    }

    /* ---------- glow sprites (replaces shadowBlur) ---------- */
    _glow(hex, r) {
      const key = hex + "|" + r;
      let cv = this._glows.get(key);
      if (!cv) {
        const R = r * 3;
        cv = document.createElement("canvas");
        cv.width = cv.height = R * 2;
        const g2 = cv.getContext("2d");
        const grad = g2.createRadialGradient(R, R, 0, R, R, R);
        grad.addColorStop(0, this._hexA(hex, 0.5));
        grad.addColorStop(0.45, this._hexA(hex, 0.16));
        grad.addColorStop(1, this._hexA(hex, 0));
        g2.fillStyle = grad;
        g2.fillRect(0, 0, R * 2, R * 2);
        this._glows.set(key, cv);
      }
      return cv;
    }

    /* ---------- animated top layer: events + signals ---------- */
    _drawEvents() {
      const ctx = this.ctx;
      const SEV = (this.colors && this.colors.severity) || SEVERITY_COLOR;
      for (const e of this.events) {
        if (this.enabledEventTypes && !this.enabledEventTypes.has(e.type)) continue;
        const [x, y] = this.screen(e.lon, e.lat);
        if (!this._inView(x, y, 30)) continue;
        const sel = this.selectedEventId === e.id;
        const r = sel ? 5 : 3.2;
        ctx.strokeStyle = sel ? this._hexA(SEV[e.severity] || "#f4b942", 0.95) : "rgba(244, 185, 66, 0.55)";
        ctx.lineWidth = sel ? 1.4 : 1;
        ctx.beginPath();
        ctx.moveTo(x, y - r); ctx.lineTo(x + r, y); ctx.lineTo(x, y + r); ctx.lineTo(x - r, y);
        ctx.closePath(); ctx.stroke();
        if (sel) {
          if (e.radius) {
            const [, y2] = this.screen(e.lon, e.lat + e.radius / 111);
            const pr = Math.abs(y - y2);
            if (pr > 6) {
              ctx.strokeStyle = "rgba(244, 185, 66, 0.3)";
              ctx.setLineDash([4, 4]);
              ctx.beginPath(); ctx.arc(x, y, pr, 0, TAU); ctx.stroke();
              ctx.setLineDash([]);
            }
          }
          ctx.strokeStyle = "rgba(244, 185, 66, 0.8)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x - 13, y); ctx.lineTo(x - 7, y); ctx.moveTo(x + 7, y); ctx.lineTo(x + 13, y);
          ctx.moveTo(x, y - 13); ctx.lineTo(x, y - 7); ctx.moveTo(x, y + 7); ctx.lineTo(x, y + 13);
          ctx.stroke();
        }
      }
    }

    _drawSignals() {
      const ctx = this.ctx;
      const SEV = (this.colors && this.colors.severity) || SEVERITY_COLOR;
      const order = [...this.signals].sort((a, b) =>
        SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
      for (const s of order) {
        const [x, y] = this.screen(s.lon, s.lat);
        if (!this._inView(x, y, 40)) continue;
        const hex = SEV[s.severity];
        const sel = this.selectedId === s.id;
        const big = s.severity === "critical" || s.severity === "high";
        const pulse = 0.5 + 0.5 * Math.sin(this.t * (s.severity === "alert" ? 0.005 : 0.003));
        if (big || sel) {
          const rr = (sel ? 10 : 7) + pulse * (s.severity === "critical" ? 10 : 6);
          ctx.strokeStyle = this._hexA(hex, (sel ? 0.5 : 0.35) * (1 - pulse * 0.5));
          ctx.lineWidth = 1.2;
          ctx.beginPath(); ctx.arc(x, y, rr, 0, TAU); ctx.stroke();
        }
        const r = sel ? 4.5 : 3.5;
        const glow = this._glow(hex, r);
        ctx.drawImage(glow, x - r * 3, y - r * 3);
        ctx.fillStyle = hex;
        ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill();
        if (sel) {
          ctx.strokeStyle = this._hexA(hex, 0.9);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x - 14, y); ctx.lineTo(x - 6, y);
          ctx.moveTo(x + 6, y); ctx.lineTo(x + 14, y);
          ctx.moveTo(x, y - 14); ctx.lineTo(x, y - 6);
          ctx.moveTo(x, y + 6); ctx.lineTo(x, y + 14);
          ctx.stroke();

          // The SITUATION around the anchor (a quake's anchor IS the epicenter):
          // dashed felt-radius ring + a spoke to every affected asset site, drawn as a
          // phosphor square with its ticker/name — so the spatial story is on the map.
          if (s.radius) {
            const [, yA] = this.screen(s.lon, s.lat - 0.5);
            const [, yB] = this.screen(s.lon, s.lat + 0.5);
            const pxPerKm = Math.abs(yA - yB) / 111.32;
            const rr = s.radius * pxPerKm;
            if (rr > 8 && rr < Math.max(this.W, this.H) * 2) {
              ctx.strokeStyle = this._hexA(hex, 0.28);
              ctx.setLineDash([5, 5]);
              ctx.lineWidth = 1;
              ctx.beginPath(); ctx.arc(x, y, rr, 0, TAU); ctx.stroke();
              ctx.setLineDash([]);
            }
          }
          const sites = (s.assets || []).filter((a) => typeof a.lat === "number" && typeof a.lon === "number").slice(0, 14);
          for (const a of sites) {
            const [ax, ay] = this.screen(a.lon, a.lat);
            ctx.strokeStyle = "rgba(95,211,224,0.35)";
            ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ax, ay); ctx.stroke();
            ctx.fillStyle = "rgba(0,0,0,0.72)";
            ctx.strokeStyle = "rgba(95,211,224,0.95)";
            ctx.lineWidth = 1.3;
            ctx.beginPath(); ctx.rect(ax - 3.5, ay - 3.5, 7, 7); ctx.fill(); ctx.stroke();
            if (this.k >= 3) {
              ctx.font = "9px 'IBM Plex Mono', monospace";
              ctx.fillStyle = "rgba(95,211,224,0.92)";
              ctx.fillText(a.ticker || (a.name || "").slice(0, 14), ax + 6, ay - 5);
            }
          }
        }
        if (this.k >= 4 && (sel || s.severity === "critical")) {
          ctx.font = "10px 'IBM Plex Mono', monospace";
          ctx.fillStyle = this._hexA(hex, 0.85);
          ctx.fillText(s.title.length > 28 ? s.title.slice(0, 27) + "…" : s.title, x + 13, y - 11);
        }
      }
    }

    /* ---------- main loop ---------- */
    loop(ts) {
      if ((!this.W || !this.proj) && this.c.clientWidth) this.resize();
      const dt = this.lastTs ? Math.min(60, ts - this.lastTs) : 16;
      this.lastTs = ts; this.t += dt;
      this.k += (this.tk - this.k) * 0.08;
      this.tx += (this.ttx - this.tx) * 0.08;
      this.ty += (this.tty - this.ty) * 0.08;
      // sim-mode motion (live positions come from the stream)
      if (!this.reduced && !this.live) {
        for (const o of this.objects) {
          if (o.domain === "satellite") {
            o.lon += o.speed * dt * 0.0016;
            if (o.lon > 180) o.lon -= 360;
            o.lat = o.amp * Math.sin(o.lon * Math.PI / 180 + o.off);
          } else {
            o.phase += o.speed * dt * 0.0004;
            const t = Math.sin(o.phase);
            o.lat = o.lat0 + o.dLat * t;
            o.lon = o.lon0 + o.dLon * t;
          }
        }
        this.posVersion++; this._gridDirty = true;
      }
      if (this.proj) this.draw(ts);
      requestAnimationFrame((t) => this.loop(t));
    }

    draw(now) {
      now = now || performance.now();
      const ctx = this.ctx, W = this.W, H = this.H, dpr = this.dpr;
      const settled = Math.abs(this.k - this.tk) < 1e-3 &&
        Math.abs(this.tx - this.ttx) < 0.4 && Math.abs(this.ty - this.tty) < 0.4;
      const have = this._rk != null;
      const scaleDev = have ? this.k / this._rk : Infinity;
      const dataSig = this._dataSig();
      const dataChanged = dataSig !== this._lastDataSig;
      const movedSinceRender = have && (this.k !== this._rk || this.tx !== this._rtx || this.ty !== this._rty);
      const idleMs = this.live ? 500 : 120;

      // Decide whether to RE-RASTERIZE the cached layers (expensive: tiles + land path).
      // Otherwise we cheaply transform-blit the existing layers — pan is free, and a
      // zoom only re-rasterizes when it has drifted enough to look soft.
      let rerender = false;
      if (!have) rerender = true;
      else if (scaleDev > 2.1 || scaleDev < 0.48) rerender = true;            // too soft mid-zoom
      else if (settled && (movedSinceRender || dataChanged)) rerender = true; // sharpen on settle
      else if (dataChanged && now - (this._lastRenderAt || 0) > idleMs) rerender = true; // live refresh at rest/pan

      if (rerender && now - (this._lastRenderAt || 0) > 30) {                 // hard ~30fps cap
        if (this.k >= 2.4) { this._ensureGrid(now); this._recordTrails(); }   // trails only when zoomed in
        this._renderStatic();
        this._renderObjects();
        this._rk = this.k; this._rtx = this.tx; this._rty = this.ty;
        this._lastDataSig = dataSig; this._lastRenderAt = now;
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      // blit cached layers transformed from their render-transform to the current one
      const s = this.k / this._rk;
      const ox = this.tx - s * this._rtx;
      const oy = this.ty - s * this._rty;
      ctx.save();
      ctx.translate(ox, oy); ctx.scale(s, s);
      ctx.drawImage(this._static, 0, 0, W, H);
      ctx.drawImage(this._obj, 0, 0, W, H);
      ctx.restore();
      // animated markers always drawn live & sharp in screen space
      if (this.layers.events) this._drawEvents();
      this._drawSignals();
    }

    /* ---------- picking ---------- */
    pick(mx, my, radius = 16) {
      let best = null, bd = radius * radius;
      for (const s of this.signals) {
        const [x, y] = this.screen(s.lon, s.lat);
        const d = (x - mx) * (x - mx) + (y - my) * (y - my);
        if (d < bd) { bd = d; best = s; }
      }
      return best;
    }
    pickEvent(mx, my, radius = 12) {
      if (!this.layers.events) return null;
      let best = null, bd = radius * radius;
      for (const e of this.events) {
        if (this.enabledEventTypes && !this.enabledEventTypes.has(e.type)) continue;
        const [x, y] = this.screen(e.lon, e.lat);
        const d = (x - mx) * (x - mx) + (y - my) * (y - my);
        if (d < bd) { bd = d; best = e; }
      }
      return best;
    }

    _hexA(hex, a) {
      const n = parseInt(hex.slice(1), 16);
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
    }
  }

